import {
    Checkbox, DefaultButton, DialogFooter,
    Dropdown,
    FocusTrapZone, IButtonProps, IconButton,
    IDropdownOption,
    ISearchBox,
    ISearchBoxStyles,
    IStyle,
    Layer,
    mergeStyles,
    Overlay,
    Panel,
    PanelType,
    Pivot,
    PivotItem,
    SearchBox,
    Separator, Stack,
    Text,
    useTheme
} from "@fluentui/react";
import { RouterParams } from 'App';
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { EControlType } from "components/ControlWrapper";
import { friendlyValue } from "global/Application";
import { useApplication } from "global/Application/Context";
import { WithRecycle } from "global/Application/dataActions";
import { setStatusHighlightField } from "global/Reducer/statusSlice";
import { bcbs_config_attribute, bcbs_config_section, bcbs_config_tab } from "model/CRM";
import { IAccountsReceivable, IBenefit, IFeesAndPremiums, IFlexibleFieldSet, IGroupConfigurationData, IGroupNumber, INonStandard, IPlan, IStopLoss } from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    connect,
    ConnectedProps,
    useDispatch,
    useSelector
} from 'react-redux';
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";

interface IFluentComponentStyles {
    root: IStyle;
}

interface ISearchCategoryItem<T> {
    parent?: any;
    name: string;
    detail: (string | undefined)[];
    primaryRoute: IRoute | null;
    deactivated?: boolean,
    hidden?: boolean,
    data: T
}

interface ISearchCategory<T> {
    entities: ISearchCategoryItem<T>[];
    tabs: ISearchCategoryItem<T>[];
    sections: ISearchCategoryItem<T>[];
    fields: ISearchCategoryItem<T>[];
    values: ISearchCategoryItem<T>[];
    vm: {
        searching: boolean;
        count: number | null;
    };
}

interface ISearchResult {
    root: ISearchCategory<any>;
    plans: ISearchCategory<any>;
    benefits: ISearchCategory<any>;
    stopLosses: ISearchCategory<any>;
    feesPremiums: ISearchCategory<any>;
    groupNumbers: ISearchCategory<any>;
    claimsARs: ISearchCategory<any>;
    nonStandards: ISearchCategory<any>;
    flexibleFieldSet: ISearchCategory<any>;
}

type TType = 'benefit' | 'plan' | 'stopLoss' | 'claimsAR' | 'feesAndPremium' | 'groupConfiguration' | 'groupNumber' | 'nonStandard' | 'flexibleFieldSet';

type TParentType =
    { entity: IBenefit, type: 'benefit' } |
    { entity: IPlan, type: 'plan' } |
    { entity: IStopLoss, type: 'stopLoss' } |
    { entity: IAccountsReceivable, type: 'claimsAR' } |
    { entity: IFeesAndPremiums, type: 'feesAndPremium' } |
    { entity: IGroupConfigurationData, type: 'groupConfiguration' }

interface IRoute {
    type: TType,
    entity?: IBenefit | IPlan | IStopLoss | IGroupConfigurationData | IGroupNumber | INonStandard | IFlexibleFieldSet,
    tab?: bcbs_config_tab,
    section?: bcbs_config_section,
    field?: bcbs_config_attribute,
    secondaryRoute?: ISecondaryRoute,

}

interface ISecondaryRoute {
    type: 'nonStandard' | 'flexibleFieldSet' | 'groupNumber',
    entity?: INonStandard | IFlexibleFieldSet | IGroupNumber,
    tab?: bcbs_config_tab,
    section?: bcbs_config_section,
    field?: bcbs_config_attribute,
}

const overflowProps: IButtonProps = { ariaLabel: 'More commands' };

const searchBoxStyles: Partial<ISearchBoxStyles> = {
    root: { width: '100%' },
};

const NameMapping = {
    groupConfiguration: 'Group Configuration',
    plan: 'Plan',
    benefit: 'Benefit',
    accountReceivable: 'Account Receivable',
    feesAndPremium: 'Fees And Premium',
    stopLoss: 'Stop Loss',
    groupNumber: 'GroupNumber',
    claimsAR: 'Account Receivable',
}

const Search = (props: TComponentConnectedProps) => {
    const ref = useRef<ISearchBox | null>(null);
    const dispatch = useDispatch();
    const searchingMutex = useRef(false);
    const theme = useTheme();

    const state = useSelector(state => state) as RootState;

    const headerCheckboxStyles: IFluentComponentStyles = useMemo(() => {
        return {
            root: {
                margin: '10px',
                minWidth: '80px',

            },
        }
    }, []);



    const [searching, setSearching] = useState(false);

    const searchTermRef = useRef('');

    const [advancedSearchHidden, setAdvancedSearchHidden] = useState(true);


    useEffect(() => {
        const debuggerOnKeyUp = (event: KeyboardEvent) => {
            if (event.code === 'KeyK' && event.ctrlKey) {
                setAdvancedSearchHidden(false);
                event.stopPropagation();
                event.stopImmediatePropagation();
                event.preventDefault();

                // waiting for mount and render to complete
                setTimeout(() => {
                    ref?.current?.focus();
                }, 100);
            }
        }

        document.addEventListener('keydown', debuggerOnKeyUp);

        return () => {
            document.removeEventListener('keydown', debuggerOnKeyUp)
        }
    }, []);


    const options = useMemo(() => {
        const entities: IDropdownOption[] = [
            { key: 'groupConfiguration', text: 'Group Configuration' },
            { key: 'plan', text: 'Plan' },
            { key: 'benefit', text: 'Benefit' },
            { key: 'accountReceivable', text: 'Account Receivable' },
            { key: 'feesAndPremium', text: 'Fees AndPremium' },
            { key: 'stopLoss', text: 'Stop Loss' },
            { key: 'groupNumber', text: 'Group Number' },
        ];

        return {
            entities,
        };

    }, []);

    // https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
    function escapeRegExp(string: string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    }

    function blankSearchModel<T>(searching: boolean): ISearchCategory<T> {
        return {
            fields: [],
            entities: [],
            sections: [],
            tabs: [],
            values: [],
            vm: {
                searching: false,
                count: 0,
            },
        }
    };

    function resetSearchResultModel(searching: boolean): ISearchResult {
        return {
            claimsARs: blankSearchModel(searching),
            benefits: blankSearchModel(searching),
            feesPremiums: blankSearchModel(searching),
            root: blankSearchModel(searching),
            groupNumbers: blankSearchModel(searching),
            plans: blankSearchModel(searching),
            stopLosses: blankSearchModel(searching),
            nonStandards: blankSearchModel(searching),
            flexibleFieldSet: blankSearchModel(searching),
        }
    }


    const history = useHistory();
    const routerParams = useParams<RouterParams>();

    const [viewModel, setViewModel] = useState({
        searchEntity: true,
        searchTab: true,
        searchSection: true,
        searchAttribute: true,
        searchAttributeValue: true,
        searchAttributeHidden: true,
        searchNonstandard: true,
        searchFlexibleFieldSet: true,
        searchLogicalName: false,
        searchContext: false,
        searchTerm: searchTermRef.current,
        searchEntities: ['groupConfiguration', 'plan', 'benefit', 'accountReceivable', 'feesAndPremium', 'stopLoss', 'groupNumber'],
    });

    const [searchResult, setSearchResult] = useState<ISearchResult>(resetSearchResultModel(false));

    // TODO make this async
    function searchEntity(
        entities: IBenefit[] | IPlan[] | IStopLoss[] | IGroupConfigurationData[] | IGroupNumber[],
        searchTerm: string,
        searchTermRegExp: RegExp,
        entityType: TType,
    ) {
        const model = blankSearchModel(true);

        entities.forEach(eachEntity => {

            if (viewModel.searchContext) {

                if (entityType === 'plan') {
                    if (eachEntity.id !== routerParams.planId)
                        return;
                }

                if (entityType === 'benefit') {
                    if (eachEntity.id !== routerParams.benefitId)
                        return;
                }

                if (entityType === 'claimsAR') {
                    if (eachEntity.id !== routerParams.claimsARId)
                        return;
                }

                if (entityType === 'feesAndPremium') {
                    if (eachEntity.id !== routerParams.feesAndPremiumId)
                        return;
                }

                if (entityType === 'stopLoss') {
                    if (eachEntity.id !== routerParams.stopLossId)
                        return;
                }

                if (entityType === 'groupConfiguration') {
                    return;
                }
            }

            const { entities, tabs, fields, sections, values } = model;

            const tabsMap = {} as Record<string, any>;
            const sectionsMap = {} as Record<string, any>;

            let entityName = (eachEntity as IBenefit | IPlan | IStopLoss | IGroupConfigurationData).name;

            if (!entityName)
                entityName = (eachEntity as IGroupNumber).groupNumber ?? '';

            // Entity
            // --------------------------
            if (searchTermRegExp.test(entityName)) {
                entities.push({
                    name: `${entityName}`,
                    detail: [entityName],
                    data: eachEntity,
                    deactivated: eachEntity.deactivated,
                    primaryRoute: {
                        type: entityType,
                        entity: eachEntity,
                    },
                });
            }

            if (eachEntity.fields)
                eachEntity.fields.forEach(eachField => {

                    // if visibility has made these hidden, lets just ignore
                    if (viewModel.searchAttributeHidden)
                        if ((eachField.visibility ?? 0) > 0)
                            return;

                    const attribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];

                    if (!attribute)
                        return;

                    const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
                    const section = state.data.config.cache.map_bcbs_config_section[attribute._bcbs_config_sectionid_value!];
                    const tab = state.data.config.cache.map_bcbs_config_tab[section._bcbs_config_tabid_value!];

                    // Tab
                    // --------------------------
                    if (viewModel.searchTab)
                        if (!tabsMap[tab.bcbs_config_tabid!])
                            if (tab?.bcbs_name && searchTermRegExp.test(tab?.bcbs_name)) {
                                tabsMap[tab.bcbs_config_tabid!] = tab;
                                tabs.push({
                                    name: tab.bcbs_name,
                                    detail: [entityName],
                                    data: tab,
                                    primaryRoute: {
                                        type: entityType,
                                        entity: eachEntity,
                                        tab: tab,
                                    },
                                });
                            }

                    // Section
                    // --------------------------
                    if (viewModel.searchSection)
                        if (!sectionsMap[section.bcbs_config_sectionid!])
                            if (searchTermRegExp.test(section.bcbs_name!)) {
                                sectionsMap[section.bcbs_config_sectionid!] = tab;
                                sections.push({
                                    name: section.bcbs_name!,
                                    detail: [entityName, tab?.bcbs_name],
                                    data: section,
                                    primaryRoute: {
                                        type: entityType,
                                        entity: eachEntity,
                                        tab: tab,
                                        section: section,
                                    },
                                });
                            }

                    // Attribute/Field
                    // --------------------------
                    if (viewModel.searchAttribute) {
                        let select = false;
                        if (searchTermRegExp.test(attribute.bcbs_name!)) {
                            select = true;
                        } else if (searchTermRegExp.test(attribute.bcbs_label!)) {
                            select = true;
                        } else if (viewModel.searchLogicalName && searchTermRegExp.test(masterAttribute.bcbs_logicalname!)) {
                            select = true;
                        } else if (searchTerm === masterAttribute.bcbs_config_masterattributeid || searchTerm === attribute.bcbs_config_attributeid) {
                            select = true;
                        }

                        // if (masterAttribute.bcbs_config_masterattributeid === "ddd9562e-7609-ee11-8f6e-000d3a305861")
                        //     debugger

                        if (select) {
                            fields.push({
                                name: attribute.bcbs_label!,
                                detail: [entityName,
                                    tab?.bcbs_name,
                                    section?.bcbs_name,
                                    attribute.bcbs_label === attribute.bcbs_name ? attribute.bcbs_name : `${attribute.bcbs_label}/${attribute.bcbs_name}`
                                ],
                                hidden: (eachField.visibility ?? 0) > 0,
                                data: attribute,
                                primaryRoute: {
                                    type: entityType,
                                    entity: eachEntity,
                                    tab: tab,
                                    section: section,
                                    field: attribute,
                                },
                            });
                        }
                    }

                    // Attribute/Field Values
                    // --------------------------
                    if (viewModel.searchAttributeValue)
                        eachField.values.forEach(field => {
                            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;

                            if (field.value) {
                                let valueString = friendlyValue(controlType, field.value);
                                if (searchTermRegExp.test(valueString!)) {
                                    values.push({
                                        name: valueString!,
                                        detail: [entityName, tab?.bcbs_name, section?.bcbs_name, `${attribute.bcbs_label}/${attribute.bcbs_name}`],
                                        hidden: (eachField.visibility ?? 0) > 0,
                                        data: attribute,
                                        primaryRoute: {
                                            type: entityType,
                                            entity: eachEntity,
                                            tab: tab,
                                            section: section,
                                            field: attribute,
                                        },
                                    });
                                }
                            }
                        });
                });

            model.vm = {
                ...model.vm,
                searching: false,
                count: fields.length + tabs.length + sections.length + entities.length + values.length,
            };
        });

        return model;
    }

    function searchChildEntity<T extends 'nonStandard' | 'flexibleFieldSet' | 'groupNumber'>(
        parent: TParentType,
        entities: T extends 'nonStandard'
            ? INonStandard[]
            : T extends 'flexibleFieldSet'
            ? IFlexibleFieldSet[]
            : IGroupNumber[],
        searchTerm: string,
        searchTermRegExp: RegExp,
        type: T,
    ) {

        const parentTypeMapped = NameMapping[parent.type];

        const model = blankSearchModel(true);

        entities.forEach(eachEntity => {

            const eachEntityNonStandard = (eachEntity as INonStandard).sectionRef && (eachEntity as INonStandard);
            const eachEntityFlexibleFieldSet = (eachEntity as IFlexibleFieldSet).attributeSetRef && (eachEntity as IFlexibleFieldSet);
            // const eachEntityGroupNumber = (eachEntity as IGroupNumber).groupNumber && (eachEntity as IGroupNumber);

            if (viewModel.searchContext) {

                if (parent.type === 'plan') {
                    if (parent.entity.id !== routerParams.planId)
                        return;
                }

                if (parent.type === 'benefit') {
                    if (parent.entity.id !== routerParams.benefitId)
                        return;
                }

                if (parent.type === 'claimsAR') {
                    if (parent.entity.id !== routerParams.claimsARId)
                        return;
                }

                if (parent.type === 'feesAndPremium') {
                    if (parent.entity.id !== routerParams.feesAndPremiumId)
                        return;
                }

                if (parent.type === 'stopLoss') {
                    if (parent.entity.id !== routerParams.stopLossId)
                        return;
                }

                if (parent.type === 'groupConfiguration') {
                    return;
                }
            }

            const { entities, tabs, fields, sections, values } = model;

            const tabsMap = {} as Record<string, any>;
            const sectionsMap = {} as Record<string, any>;

            let entityName = '';;

            // TODO needs clean up and error handling
            // this is going to be a problem, attributesets reference masterAttribute, masterAttribute can have multiple attributes...
            let parentSection: bcbs_config_section | undefined = undefined;

            if (eachEntityNonStandard) {
                parentSection = state.data.config.cache.map_bcbs_config_section[eachEntityNonStandard.sectionRef?.id!]
                entityName = eachEntityNonStandard.name;
            }

            if (eachEntityFlexibleFieldSet) {
                const attributes = state.data.config.cache.map_bcbs_config_attribute_by_masterAttributeId[eachEntityFlexibleFieldSet.attributeSetRef.id];

                if (attributes && attributes.length > 1)
                    console.warn(`Multiple attributes to host master attribute for attributeset(${eachEntityFlexibleFieldSet.attributeSetRef.id}).`);

                if (attributes)
                    parentSection = state.data.config.cache.map_bcbs_config_section[attributes[0]._bcbs_config_sectionid_value!]
                entityName = eachEntityFlexibleFieldSet.name;
            }


            const parentTab = parentSection
                ? state.data.config.cache.map_bcbs_config_tab[parentSection._bcbs_config_tabid_value!]
                : undefined;

            // Entity
            // --------------------------
            if (searchTermRegExp.test(entityName)) {
                entities.push({
                    name: `${entityName}`,
                    detail: [parentTypeMapped, parent.entity.name, parentTab?.bcbs_name, parentSection?.bcbs_name, entityName],
                    data: eachEntity,
                    deactivated: eachEntity.deactivated,
                    primaryRoute: {
                        type: parent.type,
                        entity: parent.entity,
                        tab: parentTab,
                        section: parentSection,
                        secondaryRoute: {
                            type: type,
                            entity: eachEntity,
                        }
                    },
                });
            }

            if (eachEntity.fields)
                eachEntity.fields.forEach(eachField => {

                    // if visibility has made these hidden, lets just ignore
                    if (viewModel.searchAttributeHidden)
                        if ((eachField.visibility ?? 0) > 0)
                            return;

                    const attribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];

                    if (!attribute)
                        return;

                    const masterAttribute = attribute && state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
                    const section = state.data.config.cache.map_bcbs_config_section[attribute._bcbs_config_sectionid_value!];
                    const tab = state.data.config.cache.map_bcbs_config_tab[section._bcbs_config_tabid_value!];

                    // Tab
                    // --------------------------
                    if (viewModel.searchTab)
                        if (!tabsMap[tab.bcbs_config_tabid!])
                            if (tab?.bcbs_name && searchTermRegExp.test(tab?.bcbs_name)) {
                                tabsMap[tab.bcbs_config_tabid!] = tab;
                                tabs.push({
                                    name: `${tab.bcbs_name}`,
                                    detail: [parentTypeMapped, parent.entity.name, parentTab?.bcbs_name, parentSection?.bcbs_name, entityName, tab.bcbs_name],
                                    data: eachEntity,
                                    deactivated: eachEntity.deactivated,
                                    primaryRoute: {
                                        type: parent.type,
                                        entity: parent.entity,
                                        tab: parentTab,
                                        section: parentSection,
                                        secondaryRoute: {
                                            type: type,
                                            entity: eachEntity,
                                            tab: tab,
                                            section: section,
                                        }
                                    },
                                });
                            }

                    // Section
                    // --------------------------
                    if (viewModel.searchSection)
                        if (!sectionsMap[section.bcbs_config_sectionid!])
                            if (searchTermRegExp.test(section.bcbs_name!)) {
                                sectionsMap[section.bcbs_config_sectionid!] = tab;
                                sections.push({
                                    name: `${section.bcbs_name}`,
                                    detail: [parentTypeMapped, parent.entity.name, parentTab?.bcbs_name, parentSection?.bcbs_name, entityName, tab.bcbs_name, section.bcbs_name],
                                    data: eachEntity,
                                    deactivated: eachEntity.deactivated,
                                    primaryRoute: {
                                        type: parent.type,
                                        entity: parent.entity,
                                        tab: parentTab,
                                        section: parentSection,
                                        secondaryRoute: {
                                            type: type,
                                            entity: eachEntity,
                                            tab: tab,
                                            section: section,
                                        }
                                    },
                                });
                            }


                    // Attribute/Field
                    // --------------------------
                    if (viewModel.searchAttribute) {
                        let select = false;
                        if (searchTermRegExp.test(attribute.bcbs_name!)) {
                            select = true;
                        }
                        else if (searchTermRegExp.test(attribute.bcbs_label!)) {
                            select = true;
                        } else if (viewModel.searchLogicalName && searchTermRegExp.test(masterAttribute.bcbs_logicalname!)) {
                            select = true;
                        } else if (searchTerm === masterAttribute.bcbs_config_masterattributeid || searchTerm === attribute.bcbs_config_attributeid) {
                            select = true;
                        }

                        if (select) {
                            fields.push({
                                name: `${section.bcbs_name}`,
                                detail: [parentTypeMapped, parent.entity.name, parentTab?.bcbs_name, parentSection?.bcbs_name, entityName, tab.bcbs_name, section.bcbs_name, `${attribute.bcbs_label}/${attribute.bcbs_name}`],
                                data: eachEntity,
                                deactivated: eachEntity.deactivated,
                                hidden: (eachField.visibility ?? 0) > 0,
                                primaryRoute: {
                                    type: parent.type,
                                    entity: parent.entity,
                                    tab: parentTab,
                                    section: parentSection,
                                    secondaryRoute: {
                                        type: type,
                                        entity: eachEntity,
                                        tab: tab,
                                        section: section,
                                        field: attribute,
                                    }
                                },
                            });
                        }
                    }

                    // Attribute/Field Values
                    // --------------------------
                    if (masterAttribute && viewModel.searchAttributeValue)
                        eachField.values.forEach(field => {
                            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;

                            if (field.value) {
                                let valueString = friendlyValue(controlType, field.value);
                                if (searchTermRegExp.test(valueString!)) {
                                    values.push({
                                        name: `${valueString}`,
                                        detail: [parentTypeMapped, parent.entity.name, parentTab?.bcbs_name, parentSection?.bcbs_name, entityName, tab.bcbs_name, section.bcbs_name, `${attribute.bcbs_label}/${attribute.bcbs_name}`],
                                        data: eachEntity,
                                        deactivated: eachEntity.deactivated,
                                        hidden: (eachField.visibility ?? 0) > 0,
                                        primaryRoute: {
                                            type: parent.type,
                                            entity: parent.entity,
                                            tab: parentTab,
                                            section: parentSection,
                                            secondaryRoute: {
                                                type: type,
                                                entity: eachEntity,
                                                tab: tab,
                                                section: section,
                                                field: attribute,
                                            }
                                        }
                                    });
                                }
                            }
                        });
                });

            model.vm = {
                ...model.vm,
                searching: false,
                count: fields.length + tabs.length + sections.length + entities.length + values.length,
            };
        });

        return model;
    }

    const styleSearchBox = useMemo(() => {
        return {
            width: '300px', paddingLeft: '1em', opacity: '0.9', "&:hover": { opacity: "0.9" }
        };
    }, []);

    const MergeIntoResult = function <T>(xResult: ISearchResult, model: ISearchCategory<T>, attribute: keyof ISearchResult) {
        const result = { ...xResult };
        const { entities, fields, sections, tabs, values, vm } = xResult[attribute];

        result[attribute] = {
            entities: [...entities, ...model.entities],
            fields: [...fields, ...model.fields],
            sections: [...sections, ...model.sections],
            tabs: [...tabs, ...model.tabs],
            values: [...values, ...model.values],
            vm: { ...vm, ...model.vm, count: (vm.count ?? 0) + (model.vm.count ?? 0), },
        };

        return result;
    }

    useEffect(() => {

        (async () => {
            if (searching && viewModel.searchTerm) {

                const searchTermRegExp = new RegExp(escapeRegExp(viewModel.searchTerm), 'ig');

                const json = state.data.json;

                // Reset Model
                setSearchResult(sm => {
                    return resetSearchResultModel(true);
                });

                // Group Configuration
                setImmediate(() => {

                    // for now, group configuration does not offer context id match
                    if (viewModel.searchContext)
                        return;

                    const model = searchEntity([json], viewModel.searchTerm, searchTermRegExp, 'groupConfiguration');

                    setSearchResult(x => {
                        const t = { ...x };
                        t.root = model;
                        return t;
                    });
                });

                // Plan
                setImmediate(() => {
                    const model = searchEntity(json.plans, viewModel.searchTerm, searchTermRegExp, 'plan');

                    setSearchResult(x => {
                        const t = { ...x };
                        t.plans = model;
                        return t;
                    });
                });

                // Benefit
                setImmediate(() => {
                    const model = searchEntity(json.benefits, viewModel.searchTerm, searchTermRegExp, 'benefit');

                    setSearchResult(x => {
                        const t = { ...x };
                        t.benefits = model;
                        return t;
                    });
                });

                // Stop Loss
                setImmediate(() => {
                    const model = searchEntity(json.stopLoss, viewModel.searchTerm, searchTermRegExp, 'stopLoss');

                    setSearchResult(x => {
                        const t = { ...x };
                        t.stopLosses = model;
                        return t;
                    });
                });

                // Accounts Receivable
                setImmediate(() => {
                    const model = searchEntity(json.claimsARs, viewModel.searchTerm, searchTermRegExp, 'claimsAR');

                    setSearchResult(x => {
                        const t = { ...x };
                        t.claimsARs = model;
                        return t;
                    });
                });

                // Fees And Premium
                setImmediate(() => {
                    const model = searchEntity(json.feesAndPremiums, viewModel.searchTerm, searchTermRegExp, 'feesAndPremium');

                    setSearchResult(x => {
                        const t = { ...x };
                        t.feesPremiums = model;
                        return t;
                    });
                });

                // Group Number
                setImmediate(() => {
                    const model = searchChildEntity({ entity: json, type: "groupConfiguration" }, json.groupStructure.groupNumbers, viewModel.searchTerm, searchTermRegExp, 'groupNumber');

                    setSearchResult(x => {
                        const t = { ...x };
                        t.groupNumbers = model;
                        return t;
                    });
                });

                // Group Configuration - AttributeSet
                setImmediate(() => {

                    if (!viewModel.searchFlexibleFieldSet)
                        return;

                    // for now, group configuration does not offer context id match
                    if (viewModel.searchContext)
                        return;

                    const model = searchChildEntity({ entity: json, type: "groupConfiguration" }, json.attributeSets, viewModel.searchTerm, searchTermRegExp, 'flexibleFieldSet');

                    setSearchResult(xSearchModel => {
                        return MergeIntoResult(xSearchModel, model, 'flexibleFieldSet');
                    });
                });

                // Plan - AttributeSets
                setImmediate(() => {

                    if (viewModel.searchFlexibleFieldSet)
                        json.plans.forEach(e => {

                            if (viewModel.searchContext)
                                if (e.id !== routerParams.planId)
                                    return;

                            const model = searchChildEntity({ entity: e, type: "plan" }, e.attributeSets, viewModel.searchTerm, searchTermRegExp, 'flexibleFieldSet');

                            setSearchResult(xSearchModel => {
                                return MergeIntoResult(xSearchModel, model, 'flexibleFieldSet');
                            });

                        })
                });

                // Fees And Premiums - Attribute Set
                setImmediate(() => {
                    if (viewModel.searchFlexibleFieldSet)
                        json.feesAndPremiums.forEach(e => {

                            if (viewModel.searchContext)
                                if (e.id !== routerParams.feesAndPremiumId)
                                    return;

                            const model = searchChildEntity({ entity: e, type: "feesAndPremium" }, e.attributeSets, viewModel.searchTerm, searchTermRegExp, 'flexibleFieldSet');

                            setSearchResult(x => {
                                return MergeIntoResult(x, model, 'flexibleFieldSet');
                            });

                        });

                });

                // Accounts Receivable - AttributeSets
                setImmediate(() => {
                    if (viewModel.searchFlexibleFieldSet)
                        json.claimsARs.forEach(e => {

                            if (viewModel.searchContext)
                                if (e.id !== routerParams.claimsARId)
                                    return;

                            const model = searchChildEntity({ entity: e, type: "claimsAR" }, e.attributeSets, viewModel.searchTerm, searchTermRegExp, 'flexibleFieldSet');

                            setSearchResult(xSearchModel => {
                                return MergeIntoResult(xSearchModel, model, 'flexibleFieldSet');
                            });

                        })
                });

                // Stop Loss - AttributeSets
                setImmediate(() => {
                    if (viewModel.searchFlexibleFieldSet)
                        json.stopLoss.forEach(e => {

                            if (viewModel.searchContext)
                                if (e.id !== routerParams.stopLossId)
                                    return;

                            const model = searchChildEntity({ entity: e, type: "stopLoss" }, e.attributeSets, viewModel.searchTerm, searchTermRegExp, 'flexibleFieldSet');

                            setSearchResult(xSearchModel => {
                                return MergeIntoResult(xSearchModel, model, 'flexibleFieldSet');
                            });
                        })
                });

                // Benefit - AttributeSets
                setImmediate(() => {

                    if (viewModel.searchFlexibleFieldSet)
                        json.benefits.forEach(e => {

                            if (viewModel.searchContext)
                                if (e.id !== routerParams.benefitId)
                                    return;

                            const model = searchChildEntity({ entity: e, type: "benefit" }, e.attributeSets, viewModel.searchTerm, searchTermRegExp, 'flexibleFieldSet');

                            setSearchResult(xSearchModel => {
                                return MergeIntoResult(xSearchModel, model, 'flexibleFieldSet');
                            });

                        })
                });

                // Benefit - NonStandards
                setImmediate(() => {

                    json.benefits.forEach(e => {

                        if (viewModel.searchContext)
                            if (e.id !== routerParams.benefitId)
                                return;

                        const model = searchChildEntity({ entity: e, type: "benefit" }, e.nonStandards, viewModel.searchTerm, searchTermRegExp, 'nonStandard');

                        setSearchResult(x => {
                            return MergeIntoResult(x, model, 'nonStandards');
                        });

                    });

                });

                // Group Configuration - NonStandards
                setImmediate(() => {

                    if (!viewModel.searchNonstandard)
                        return;

                    // for now, group configuration does not offer context id match
                    if (viewModel.searchContext)
                        return;

                    const model = searchChildEntity({ entity: json, type: "groupConfiguration" }, json.nonStandards, viewModel.searchTerm, searchTermRegExp, 'nonStandard');

                    setSearchResult(xSearchModel => {
                        return MergeIntoResult(xSearchModel, model, 'nonStandards');
                    });

                });

                setSearching(false)
            }
        })();

    }, [searching]);


    const itemClassName = useMemo(() => mergeStyles({
        // marginBottom: '10px',
        padding: '8px',
        cursor: 'pointer',
        borderTop: '1px solid rgba(100,100,100,0.1)',
        ":hover": {
            background: theme.palette.white,
            transition: '0.2s',
            cursor: 'pointer',
            zIndex: 100
        },
    }), [theme]);


    const onRenderFooterContent = () => {
        if (searching)
            return null;

        return (
            <DialogFooter>
                <DefaultButton onClick={onDismiss}>
                    Cancel
                </DefaultButton>
            </DialogFooter>
        )
    }

    const onRenderPanelWrap = (child: JSX.Element) => {
        return <Panel
            isOpen={true}
            onDismiss={() => { setAdvancedSearchHidden(true); }}
            type={PanelType.large}
            closeButtonAriaLabel="Close"
            headerText="Search | Group Configuration"

        >
            <Separator></Separator>
            <div style={{ margin: '15px' }}></div>
            {child}
        </Panel>
    }

    const onDismiss = () => {
        setAdvancedSearchHidden(true)
    }

    const onRenderScreenWrap = (child: JSX.Element) => {
        return <Layer>
            <Overlay styles={{ root: { background: theme.semanticColors.bodyBackground } }}>
            </Overlay>
            <FocusTrapZone style={{ position: 'absolute', height: '100%', width: '100%' }}>
                <Stack styles={{ root: { padding: '15px', height: '100%', width: '100%' } }}>
                    <Stack.Item align="end" styles={{ root: { marginBottom: '10px' } }}>
                        <IconButton
                            iconProps={{ iconName: "ChromeClose" }}
                            onClick={(event) => {
                                onDismiss();
                            }}
                        />
                    </Stack.Item>
                    <Stack.Item styles={{ root: { height: '100%', width: '100%' } }}>
                        {child}
                    </Stack.Item>
                </Stack>
            </FocusTrapZone>
        </Layer>
    }

    const onRenderEntities = (category: string, entities: ISearchCategoryItem<any>[]) => {
        const title = entities.length > 0 ? `${category} (${entities.length})` : category;

        return <CustomPanel
            title={title}
            canCollapse={true}
            defaultCollapsed={true}
            mountWhenExpanded={true}
        >
            {entities.map(eachEntity => {
                return <div
                    // key={eachEntity.}
                    className={itemClassName}
                    onClick={() => {

                        const primaryRoute = eachEntity.primaryRoute;

                        if (primaryRoute && primaryRoute.type) {
                            let path = `/records/blueRelationship/${state.data.snapshot._bcbs_groupid_value}`;

                            path += `/groupConfiguration/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}`;

                            if (primaryRoute.type !== 'groupConfiguration')
                                if (primaryRoute.entity)
                                    path += `/${primaryRoute.type}/${primaryRoute.entity.id}`;

                            if (primaryRoute.tab)
                                path += `/tab/${primaryRoute.tab.bcbs_config_tabid}`;

                            if (primaryRoute.section)
                                path += `/section/${primaryRoute.section.bcbs_config_sectionid}`;

                            if (primaryRoute.field)
                                path += `/field/${primaryRoute.field.bcbs_config_attributeid}`;

                            const secondaryRoute = primaryRoute.secondaryRoute;

                            if (secondaryRoute) {

                                if (secondaryRoute.type)
                                    path += `/${secondaryRoute.type}/${secondaryRoute.entity?.id}`;

                                if (secondaryRoute.tab)
                                    path += `/tab/${secondaryRoute.tab.bcbs_config_tabid}`;

                                if (secondaryRoute.section)
                                    path += `/section/${secondaryRoute.section.bcbs_config_sectionid}`;

                                if (secondaryRoute.field)
                                    path += `/field/${secondaryRoute.field.bcbs_config_attributeid}`;
                            }

                            if (secondaryRoute?.field?.bcbs_config_attributeid) {
                                dispatch(setStatusHighlightField(secondaryRoute?.field?.bcbs_config_attributeid))
                            } else if (primaryRoute?.field?.bcbs_config_attributeid) {
                                dispatch(setStatusHighlightField(primaryRoute?.field?.bcbs_config_attributeid))
                            }

                            dispatch(
                                WithRecycle(() => {
                                    history?.push({ pathname: path, search: '?navigation=pending' });
                                }, 100)
                            );

                            onDismiss();
                        }
                    }}
                >
                    <Stack tokens={{ childrenGap: '4px' }}>
                        {(eachEntity.hidden || eachEntity.deactivated) &&
                            <div style={{ position: 'relative', width: '0', height: '0', left: '-23px' }}>
                                <div>
                                    🏴
                                </div>
                            </div>
                        }
                        <Text>{eachEntity.name}</Text>
                        <Text variant="xSmall">{
                            eachEntity.detail.map(eachEntityDetail => {
                                if (eachEntityDetail)
                                    return <span>{eachEntityDetail} &gt; </span>
                                    
                                return null;
                            })}
                        </Text>
                        {/* <Stack horizontal tokens={{ childrenGap: '4px', }} wrap>
                            {eachEntity.detail.map(t => {
                                return <Text className={secondaryClass} variant="xSmall" >
                                    {t}
                                </Text>
                            })}
                        </Stack> */}
                    </Stack>
                </div>

            })}
        </CustomPanel>;

    }

    const onRenderSearchModel = (t: ISearchCategory<any>) => {
        if (t.vm.searching)
            return <>Searching...</>;

        return <>
            <br />
            {t.entities.length > 0 &&
                onRenderEntities(`Entities`, t.entities)
            }
            {t.tabs.length > 0 &&
                onRenderEntities(`Tabs`, t.tabs)
            }
            {t.sections.length > 0 &&
                onRenderEntities(`Sections`, t.sections)
            }
            {t.fields.length > 0 &&
                onRenderEntities(`Attributes`, t.fields)
            }
            {t.values.length > 0 &&
                onRenderEntities(`Values`, t.values)
            }
        </>;
    }

    const PivotItemSearchModel = (name: string, model: ISearchCategory<any>) => {
        if (model.vm.count === 0)
            return undefined;

        return <PivotItem headerText={`${name} (${model.vm.count})`}>
            {onRenderSearchModel(model)}
        </PivotItem>;
    }

    const onRenderAdvancedSearch = () => {
        return <Stack style={{ height: '100%', width: '100%' }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: '8px' }}>
                <Stack.Item grow={1}>
                    <SearchBox
                        componentRef={ref}
                        placeholder="Search"
                        defaultValue={viewModel.searchTerm}
                        styles={searchBoxStyles}
                        onChange={(event, newValue) => {
                            searchTermRef.current = newValue!;
                        }}
                        onSearch={(newValue) => {
                            searchTermRef.current = newValue!;
                            setViewModel(vm => ({ ...vm, searchTerm: searchTermRef.current }));
                            setSearching(true);
                        }}
                    />
                </Stack.Item>
                <Stack.Item>
                    <DefaultButton
                        text="Search"
                        split
                        onClick={() => {
                            setViewModel(vm => ({ ...vm, searchTerm: searchTermRef.current }));
                            setSearching(true);
                        }}
                        menuProps={{
                            items: [
                                {
                                    key: 'entities',
                                    onRender: (item, dismissMenu) => {
                                        return <Dropdown
                                            label="Entities"
                                            selectedKeys={viewModel.searchEntities}
                                            multiSelect={true}
                                            disabled
                                            styles={{ root: { margin: '10px', minWidth: '300px', maxWidth: '300px' } }}
                                            options={options.entities}
                                            onChange={(event, option) => {

                                                setViewModel(t => {

                                                    let entities = [...t.searchEntities];

                                                    if (option && option.selected === true)
                                                        entities.push(option.key as string);

                                                    if (option && option.selected === false)
                                                        entities = entities.filter(q => q !== option.key as string)

                                                    return {
                                                        ...t, searchEntities: entities
                                                    }
                                                });

                                            }}
                                        />

                                    }
                                },
                                {
                                    key: 'tabs',
                                    onRender: (item, dismissMenu) => {
                                        return <Checkbox
                                            inputProps={{ role: 'menuitemcheckbox' }}
                                            label="Search Tabs"
                                            styles={headerCheckboxStyles}
                                            checked={viewModel.searchTab}
                                            onChange={(event, checked) => setViewModel({ ...viewModel, searchTab: !!checked! })}
                                        />
                                    }
                                },
                                {
                                    key: 'sections',
                                    onRender: (item, dismissMenu) => {
                                        return <Checkbox
                                            inputProps={{ role: 'menuitemcheckbox' }}
                                            label="Search Sections"
                                            styles={headerCheckboxStyles}
                                            checked={viewModel.searchSection}
                                            onChange={(event, checked) => setViewModel({ ...viewModel, searchSection: checked! })}
                                        />
                                    }
                                },

                                {
                                    key: 'attributes',
                                    onRender: (item, dismissMenu) => {
                                        return <Checkbox
                                            inputProps={{ role: 'menuitemcheckbox' }}
                                            label="Search Attributes"
                                            styles={headerCheckboxStyles}
                                            checked={viewModel.searchAttribute}
                                            onChange={(event, checked) => setViewModel({ ...viewModel, searchAttribute: checked! })}
                                        />
                                    }
                                },
                                {
                                    key: 'attributeValues',
                                    onRender: (item, dismissMenu) => {
                                        return <Checkbox
                                            inputProps={{ role: 'menuitemcheckbox' }}
                                            label="Search Values"
                                            styles={headerCheckboxStyles}
                                            checked={viewModel.searchAttributeValue}
                                            onChange={(event, checked) => setViewModel({ ...viewModel, searchAttributeValue: checked! })}
                                        />
                                    }
                                },
                                {
                                    key: 'nonStandards',
                                    onRender: (item, dismissMenu) => {
                                        return <Checkbox
                                            inputProps={{ role: 'menuitemcheckbox' }}
                                            label="Search Non-Standards"
                                            styles={headerCheckboxStyles}
                                            checked={viewModel.searchNonstandard}
                                            onChange={(event, checked) => setViewModel({ ...viewModel, searchNonstandard: checked! })}
                                        />
                                    }
                                },

                                {
                                    key: 'attributeSets',
                                    onRender: (item, dismissMenu) => {
                                        return <Checkbox
                                            inputProps={{ role: 'menuitemcheckbox' }}
                                            label="Search Attribute-Sets"
                                            styles={headerCheckboxStyles}
                                            checked={viewModel.searchFlexibleFieldSet}
                                            onChange={(event, checked) => setViewModel({ ...viewModel, searchFlexibleFieldSet: checked! })}
                                        />
                                    }
                                },

                                {
                                    key: 'logicalNames',
                                    onRender: (item, dismissMenu) => {
                                        return <Checkbox
                                            inputProps={{ role: 'menuitemcheckbox' }}
                                            label="Search Logical Names"
                                            styles={headerCheckboxStyles}
                                            checked={viewModel.searchLogicalName}
                                            onChange={(event, checked) => setViewModel({ ...viewModel, searchLogicalName: checked! })}
                                        />
                                    }
                                },


                                {
                                    key: 'visible',
                                    onRender: (item, dismissMenu) => {
                                        return <Checkbox
                                            inputProps={{ role: 'menuitemcheckbox' }}
                                            label="Search Only Visible"
                                            styles={headerCheckboxStyles}
                                            checked={viewModel.searchAttributeHidden}
                                            onChange={(event, checked) => setViewModel({ ...viewModel, searchAttributeHidden: checked! })}
                                        />
                                    }
                                },
                                {
                                    key: 'context',
                                    onRender: (item, dismissMenu) => {
                                        return <Checkbox
                                            inputProps={{ role: 'menuitemcheckbox' }}
                                            label="Search Current Context"
                                            styles={headerCheckboxStyles}
                                            checked={viewModel.searchContext}
                                            onChange={(event, checked) => setViewModel({ ...viewModel, searchContext: checked! })}
                                        />
                                    }
                                },
                            ],
                        }}
                    />
                </Stack.Item>
            </Stack>

            {searching &&
                <Stack.Item>
                    Searching
                </Stack.Item>
            }

            <Stack.Item>
                <Separator />
            </Stack.Item>

            <Stack.Item style={{ height: '100%', width: '100%', overflowY: 'auto', marginBottom: '35px' }}>
                <Pivot overflowBehavior={'menu'}>
                    {PivotItemSearchModel('Group Configuration', searchResult.root)}
                    {PivotItemSearchModel('Plans', searchResult.plans)}
                    {PivotItemSearchModel('Benefits', searchResult.benefits)}
                    {PivotItemSearchModel('Accounts Receivables', searchResult.claimsARs)}
                    {PivotItemSearchModel('Group Numbers', searchResult.groupNumbers)}
                    {PivotItemSearchModel('Fees & Premiums', searchResult.feesPremiums)}
                    {PivotItemSearchModel('Stop Loss', searchResult.stopLosses)}
                    {PivotItemSearchModel('Non Standards', searchResult.nonStandards)}
                    {PivotItemSearchModel('AttributeSets', searchResult.flexibleFieldSet)}
                </Pivot>
            </Stack.Item>
        </Stack>

    }

    const onRenderSearchBox = () => {
        return <div style={styleSearchBox} >
            <SearchBox
                placeholder="Search (Ctrl+K)"
                onSearch={newValue => {
                    searchTermRef.current = newValue ?? '';
                    setViewModel(vm => ({ ...vm, searchTerm: searchTermRef.current }));
                    setAdvancedSearchHidden(false);
                    setSearching(true);
                }}
                onChange={(event, newValue) => {
                    searchTermRef.current = newValue!;
                }}
            />
        </div>;
    }

    if (advancedSearchHidden)
        return onRenderSearchBox();

    return onRenderPanelWrap(onRenderAdvancedSearch());
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(Search);